import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import Button from "@/components/Core/Button/Button";
import { cn } from "@/lib/utils";
import { useOrganisation, useUpdateOrganisation } from "@/networking/organisation.hooks";
import { SettingsLeaf } from "./SettingsLeafLayout";

interface ToggleProps {
	checked: boolean;
	disabled?: boolean;
	label: string;
	onChange: (checked: boolean) => void;
}

/** Switch-styled on/off control — a policy is either enforced or not. */
const Toggle: React.FC<ToggleProps> = ({ checked, disabled, label, onChange }) => (
	<button
		type="button"
		role="switch"
		aria-checked={checked}
		aria-label={label}
		disabled={disabled}
		onClick={() => onChange(!checked)}
		className={cn(
			"relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-solid border-transparent transition-colors",
			"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2",
			checked ? "bg-brand" : "bg-gray-300",
			disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
		)}
	>
		<span
			aria-hidden="true"
			className={cn(
				"inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
				checked ? "translate-x-[22px]" : "translate-x-[2px]"
			)}
		/>
	</button>
);

/**
 * "Säkerhet" — organisation-wide login policy.
 *
 * The only setting today is `securitySettings.requireTwoFactor`. It is enforced
 * server-side at login (oo-user-service.get-auth-user hands the auth service a
 * user with `twoFactor: "sms"` for every internal user of the org), so the
 * per-user 2FA checkbox on the user page becomes irrelevant while this is on,
 * and switching it off here takes effect at the next login without touching
 * any user. Consultants (app) and customer users are not affected.
 *
 * There is no backfill: an org without `securitySettings` enforces nothing,
 * which is what every org did before the field existed.
 */
const SakerhetPage = () => {
	const { t } = useTranslation();
	const { data: organisation, isLoading } = useOrganisation();
	const { mutate: updateOrganisation, isLoading: isSaving } = useUpdateOrganisation();

	const saved = organisation?.securitySettings?.requireTwoFactor ?? false;
	const [requireTwoFactorDraft, setRequireTwoFactorDraft] = useState(false);

	useEffect(() => {
		setRequireTwoFactorDraft(saved);
	}, [saved]);

	const hasUnsavedChanges = requireTwoFactorDraft !== saved;

	// The whole nested object is replaced on save (the org PUT $sets top-level
	// fields), so always send it complete rather than patching into it.
	const save = () =>
		updateOrganisation({ securitySettings: { requireTwoFactor: requireTwoFactorDraft } });

	return (
		<SettingsLeaf
			title="Säkerhet"
			description="Inloggningsregler som gäller alla användare av dashboarden i organisationen. Påverkar inte konsulternas app eller kundernas inloggning."
		>
			<div className="flex items-start justify-between gap-6 rounded-lg border border-solid border-gray-200 bg-white p-4">
				<div className="min-w-0">
					<div className="text-sm font-medium text-gray-900">
						Kräv tvåfaktorsinloggning via SMS
					</div>
					<p className="mt-1 text-sm leading-snug text-gray-500">
						Alla administratörer och konsultchefer måste ange en engångskod som skickas via
						SMS vid varje inloggning, oavsett om de själva aktiverat tvåfaktorsinloggning på
						sitt konto.
					</p>
				</div>
				<Toggle
					checked={requireTwoFactorDraft}
					disabled={isLoading || isSaving}
					label="Kräv tvåfaktorsinloggning via SMS"
					onChange={setRequireTwoFactorDraft}
				/>
			</div>

			{requireTwoFactorDraft && (
				<div className="flex gap-3 rounded-lg border border-solid border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
					<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
					<p className="leading-snug">
						Användare utan mobilnummer kan inte logga in medan regeln är aktiv. Kontrollera
						under <span className="font-medium">Användare</span> att alla har ett nummer
						innan du sparar – du kan lägga till numret på respektive användare.
					</p>
				</div>
			)}

			<div>
				<Button onClick={save} disabled={!hasUnsavedChanges || isSaving} height="37px" fontSize="s">
					{t("SAVE_CHANGES")}
				</Button>
			</div>
		</SettingsLeaf>
	);
};

export default SakerhetPage;
